import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddPhotoAlternateOutlined,
  CheckCircle,
  Close as CloseIcon,
  ErrorOutline,
  FileUploadOutlined,
  Search as SearchIcon,
} from "@mui/icons-material";
import { baseAxios } from "../../utils/config";
import {
  ACCEPT_ATTRIBUTE,
  matchFilesToProducts,
  validateImageFile,
} from "./productImageHelpers";

const PAGE_SIZE = 25;
const MAX_CONCURRENT_UPLOADS = 3;
const UNMATCHED_DRAG_TYPE = "application/x-unmatched-image";

const thumbnailSx = {
  width: 36,
  height: 36,
  objectFit: "cover",
  borderRadius: 1,
  border: "1px solid",
  borderColor: "divider",
  flexShrink: 0,
};

function defaultDescription({ withImage, total }) {
  return `${withImage} of ${total} products have an image.`;
}

/**
 * Attach pictures to products. Drop many files at once and any file named
 * after a barcode attaches automatically; the rest can be dragged onto a row,
 * or a row can be clicked to pick a file. Every image is its own request.
 *
 * Used as the optional step after a products import and, on its own, by the
 * Add images dialog on Manage Products. A bulk-dropped file that matches a
 * product which already has an image is held back until the user chooses to
 * replace or skip it; dropping on a row or picking from a row replaces
 * directly.
 *
 * @param {{product_id: number, barcode: string, product_name: string|null, brand: string, is_retail: boolean, product_image?: string|null}[]} products
 * @param {(counts: {withImage: number, total: number}) => React.ReactNode} [description]  sentence above the drop zone
 * @param {(product: object, url: string) => void} [onImageUploaded]  called after each successful upload
 * @param {(busy: number) => void} [onBusyChange]  called with the number of uploads queued or in flight
 */
export default function ProductImagesStep({
  products,
  description = defaultDescription,
  onImageUploaded,
  onBusyChange,
}) {
  // product_id -> { status: "uploading" | "done" | "failed", url?, error?, file? }
  // `url` is the image the product currently has on the server: while a
  // replacement uploads or after it fails, that is still the old one.
  // Products not listed here fall back to their saved product_image.
  const [images, setImages] = useState({});
  // [{ id, file, reason }]
  const [unmatched, setUnmatched] = useState([]);
  // [{ id, file, product }] bulk-dropped files for products that already have an image
  const [conflicts, setConflicts] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const bulkInputRef = useRef(null);
  const rowInputRef = useRef(null);
  const rowTargetRef = useRef(null);
  const entrySeq = useRef(0);

  // Session state first, then the image the product already had.
  const stateOf = useCallback(
    (product) => {
      const state = images[product.product_id];
      if (state) return state;
      return product.product_image
        ? { status: "existing", url: product.product_image }
        : undefined;
    },
    [images]
  );

  // Products that have an image on the server right now.
  const withImageCount = useMemo(
    () => products.filter((p) => Boolean(stateOf(p)?.url)).length,
    [products, stateOf]
  );

  // --- Upload queue, at most MAX_CONCURRENT_UPLOADS in flight ---

  const reportBusy = useCallback(() => {
    if (onBusyChange) {
      onBusyChange(activeRef.current + queueRef.current.length);
    }
  }, [onBusyChange]);

  const uploadOne = useCallback(
    async ({ product, file }) => {
      try {
        const formData = new FormData();
        formData.append("productImage", file);
        const res = await baseAxios.post(
          `/products/${product.product_id}/image`,
          formData,
          { headers: { "Content-Type": undefined } }
        );
        const url = res.data?.product_image;
        setImages((current) => ({
          ...current,
          [product.product_id]: { status: "done", url },
        }));
        if (onImageUploaded) onImageUploaded(product, url);
      } catch (err) {
        setImages((current) => ({
          ...current,
          [product.product_id]: {
            status: "failed",
            url: current[product.product_id]?.url,
            file,
            error:
              err?.response?.data?.error ||
              err?.data?.error ||
              err?.response?.data?.message ||
              "Upload failed",
          },
        }));
      }
    },
    [onImageUploaded]
  );

  const pump = useCallback(() => {
    while (
      activeRef.current < MAX_CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const job = queueRef.current.shift();
      activeRef.current += 1;
      uploadOne(job).finally(() => {
        activeRef.current -= 1;
        reportBusy();
        pump();
      });
    }
  }, [uploadOne, reportBusy]);

  const enqueue = useCallback(
    (jobs) => {
      if (jobs.length === 0) return;
      setImages((current) => {
        const next = { ...current };
        for (const { product } of jobs) {
          next[product.product_id] = {
            status: "uploading",
            url: current[product.product_id]?.url ?? product.product_image,
          };
        }
        return next;
      });
      queueRef.current.push(...jobs);
      reportBusy();
      pump();
    },
    [pump, reportBusy]
  );

  const nextId = () => {
    entrySeq.current += 1;
    return entrySeq.current;
  };

  const addUnmatched = useCallback((entries) => {
    if (entries.length === 0) return;
    setUnmatched((current) => [
      ...current,
      ...entries.map((entry) => ({ id: nextId(), ...entry })),
    ]);
  }, []);

  const addConflicts = useCallback((entries) => {
    if (entries.length === 0) return;
    setConflicts((current) => [
      ...current,
      ...entries.map((entry) => ({ id: nextId(), ...entry })),
    ]);
  }, []);

  // --- Bulk drop: match by barcode in the filename ---

  const handleBulkFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return;
      // Tell the matcher which products already have an image, so their
      // files come back as conflicts instead of uploads.
      const candidates = products.map((p) => ({
        ...p,
        product_image: stateOf(p)?.url || null,
      }));
      const result = matchFilesToProducts(files, candidates);

      // One upload per product at a time: a product that is uploading, or
      // already has a file waiting for a decision, cannot take another.
      const waiting = new Set(conflicts.map((c) => c.product.product_id));
      const matched = [];
      const newConflicts = [];
      const unmatchedNow = [...result.unmatched];
      for (const entry of [...result.matched, ...result.conflicts]) {
        const id = entry.product.product_id;
        if (stateOf(entry.product)?.status === "uploading") {
          unmatchedNow.push({
            file: entry.file,
            reason: "An upload for this product is in progress",
          });
        } else if (waiting.has(id)) {
          unmatchedNow.push({
            file: entry.file,
            reason: "Another file is already waiting for this product",
          });
        } else if (entry.product.product_image) {
          newConflicts.push(entry);
        } else {
          matched.push(entry);
        }
      }
      enqueue(matched);
      addConflicts(newConflicts);
      addUnmatched(unmatchedNow);
    },
    [products, conflicts, stateOf, enqueue, addConflicts, addUnmatched]
  );

  // --- Conflicts: the user decides per file ---

  const resolveConflicts = useCallback(
    (ids, replace) => {
      const chosen = conflicts.filter((c) => ids.includes(c.id));
      setConflicts((current) => current.filter((c) => !ids.includes(c.id)));
      if (!replace) return;
      // A row upload may have started for this product since the drop.
      const ready = chosen.filter(
        (c) => stateOf(c.product)?.status !== "uploading"
      );
      addUnmatched(
        chosen
          .filter((c) => !ready.includes(c))
          .map((c) => ({
            file: c.file,
            reason: "An upload for this product is in progress",
          }))
      );
      enqueue(ready.map(({ product, file }) => ({ product, file })));
    },
    [conflicts, stateOf, enqueue, addUnmatched]
  );

  // --- One product: click to pick, or drop a file / an unmatched entry on the row ---

  const attachToProduct = useCallback(
    (product, file) => {
      const invalid = validateImageFile(file);
      if (invalid) {
        setImages((current) => ({
          ...current,
          [product.product_id]: {
            status: "failed",
            url: current[product.product_id]?.url ?? product.product_image,
            error: invalid,
          },
        }));
        return;
      }
      enqueue([{ product, file }]);
    },
    [enqueue]
  );

  const handleRowDrop = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const unmatchedId = Number(e.dataTransfer.getData(UNMATCHED_DRAG_TYPE));
    if (unmatchedId) {
      const entry = unmatched.find((u) => u.id === unmatchedId);
      if (entry) {
        setUnmatched((current) => current.filter((u) => u.id !== unmatchedId));
        attachToProduct(product, entry.file);
      }
      return;
    }
    const file = e.dataTransfer.files?.[0];
    if (file) attachToProduct(product, file);
  };

  const openRowPicker = (product) => {
    rowTargetRef.current = product;
    rowInputRef.current?.click();
  };

  // --- Filtering and paging ---

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return products;
    return products.filter((p) =>
      [p.barcode, p.product_name, p.brand]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [products, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const labelOf = (product) => product.product_name || product.barcode;

  const renderPickButton = (product, title) => (
    <Tooltip title={title}>
      <IconButton
        size="small"
        aria-label={`${title} for ${labelOf(product)}`}
        onClick={() => openRowPicker(product)}
      >
        <AddPhotoAlternateOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderSlot = (product) => {
    const state = stateOf(product);
    if (state?.status === "uploading") {
      return <CircularProgress size={22} />;
    }
    if (state?.status === "done" || state?.status === "existing") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {state.url ? (
            <Box component="img" src={state.url} alt="" sx={thumbnailSx} />
          ) : null}
          {state.status === "done" && (
            <CheckCircle fontSize="small" color="success" />
          )}
          {renderPickButton(product, "Replace image")}
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {state?.url ? (
          <Box component="img" src={state.url} alt="" sx={thumbnailSx} />
        ) : null}
        {renderPickButton(
          product,
          state?.error ? "Try again" : "Choose an image"
        )}
        {state?.status === "failed" && (
          <Tooltip title={state.error}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "error.main",
              }}
            >
              <ErrorOutline fontSize="small" />
              <Typography variant="caption" color="error">
                {state.error}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {description({ withImage: withImageCount, total: products.length })}
      </Typography>

      {/* Bulk drop zone */}
      <Box
        onClick={() => bulkInputRef.current?.click()}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleBulkFiles(e.dataTransfer.files);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        sx={{
          border: "2px dashed",
          borderColor: isDragging ? "primary.main" : "#ccc",
          borderRadius: 2,
          p: 3,
          mb: 2,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragging ? "action.hover" : "transparent",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
      >
        <FileUploadOutlined sx={{ fontSize: 36, color: "text.secondary" }} />
        <Typography variant="body1" fontWeight={500}>
          Drop product images here, or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Files named after a barcode, like 012345678905.jpg, attach to that
          product automatically.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          JPEG, PNG or WebP, up to 4 MB each
        </Typography>
        <input
          ref={bulkInputRef}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          multiple
          hidden
          onChange={(e) => {
            handleBulkFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>

      {/* One shared picker for the per-row buttons */}
      <input
        ref={rowInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && rowTargetRef.current) {
            attachToProduct(rowTargetRef.current, file);
          }
          e.target.value = "";
        }}
      />

      {conflicts.length > 0 && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="body2">
              {conflicts.length}{" "}
              {conflicts.length === 1 ? "file matches a product" : "files match products"}{" "}
              that already {conflicts.length === 1 ? "has" : "have"} an image.
              Replace or skip each one.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  resolveConflicts(
                    conflicts.map((c) => c.id),
                    true
                  )
                }
              >
                Replace all
              </Button>
              <Button
                size="small"
                onClick={() =>
                  resolveConflicts(
                    conflicts.map((c) => c.id),
                    false
                  )
                }
              >
                Skip all
              </Button>
            </Box>
          </Box>
          <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
            {conflicts.map((entry) => {
              const current = stateOf(entry.product);
              return (
                <Box
                  key={entry.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.5,
                  }}
                >
                  {current?.url ? (
                    <Box
                      component="img"
                      src={current.url}
                      alt=""
                      sx={thumbnailSx}
                    />
                  ) : (
                    <Box sx={thumbnailSx} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {labelOf(entry.product)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {entry.file.name}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => resolveConflicts([entry.id], true)}
                  >
                    Replace
                  </Button>
                  <Button
                    size="small"
                    onClick={() => resolveConflicts([entry.id], false)}
                  >
                    Skip
                  </Button>
                </Box>
              );
            })}
          </Box>
        </Alert>
      )}

      {unmatched.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            {unmatched.length} {unmatched.length === 1 ? "file was" : "files were"}{" "}
            not attached. Drag one onto a product below, or dismiss it.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {unmatched.map((entry) => (
              <Tooltip key={entry.id} title={entry.reason}>
                <Chip
                  label={entry.file.name}
                  size="small"
                  variant="outlined"
                  draggable
                  onDragStart={(e) =>
                    e.dataTransfer.setData(UNMATCHED_DRAG_TYPE, String(entry.id))
                  }
                  onDelete={() =>
                    setUnmatched((current) =>
                      current.filter((u) => u.id !== entry.id)
                    )
                  }
                  deleteIcon={<CloseIcon />}
                  sx={{ cursor: "grab" }}
                />
              </Tooltip>
            ))}
          </Box>
        </Alert>
      )}

      <TextField
        size="small"
        fullWidth
        placeholder="Search by barcode, name or brand"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Barcode</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Image</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No products match your search
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((product) => (
                <TableRow
                  key={product.product_id}
                  hover
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleRowDrop(e, product)}
                >
                  <TableCell>{product.barcode}</TableCell>
                  <TableCell>
                    {product.product_name || "—"}{" "}
                    {product.is_retail && (
                      <Chip label="retail" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>{renderSlot(product)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={(_, value) => setPage(value)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
